<template>
  <b-sidebar
    id="sidebar"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    no-close-on-backdrop
    right
    @shown="loadData"
    @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0 text-capitalize">
          {{
            (isAdd ? $t("actions.add") : $t("actions.edit")) +
            " " +
            $tc("pacom.typeAlarm", 1)
          }}
        </h5>

        <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
      </div>
      <!-- BODY -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
          @reset.prevent="resetForm"
        >

          <!-- Name -->
          <validation-provider
            v-if="!isAdd"
            #default="validationContext"
            name="firstname"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('pacom.name')"
              label-for="firstName"
            >
              <b-form-input
                id="firstName"
                v-model="itemData.name"
                :state="getValidationState(validationContext)"
                trim
                disabled
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Description -->
          <validation-provider
            v-if="!isAdd"
            #default="validationContext"
            name="description"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('pacom.description')"
              label-for="firstName"
            >
              <b-form-input
                id="description"
                v-model="itemData.description"
                :state="getValidationState(validationContext)"
                trim
                disabled
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

           <!-- Priority -->
           <validation-provider
            v-if="!isAdd"
            #default="validationContext"
            name="priority"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('pacom.priority')"
              label-for="lastName"
            >
              <b-form-input
                id="priority"
                v-model="itemData.priority"
                :state="getValidationState(validationContext)"
                trim
                disabled
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Type alarm -->
          <validation-provider
            #default="validationContext"
            name="id_type_alarm"
            rules="required"
          >
            <b-form-group
              :label="$t('pacom.typeAlarm')"
              label-for="id_type_alarm"
              :state="getValidationState(validationContext)"
            >
              <v-select
                label="description"
                v-model="itemData.id_type_alarm"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="typeAlarms"
                :clearable="false"
                :reduce="(val) => val.id"
                input-id="id_type_alarm"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback >
            </b-form-group>
          </validation-provider>

          <!-- Dsipatch alarm -->
          <validation-provider
            #default="validationContext"
            name="groups"
            rules="required"
          >
            <b-form-group
                :label="$t('pacom.dispatchAlarm')"
                label-for="groups"
                :state="getValidationState(validationContext)"
            >
              <b-form-checkbox
                  v-model="itemData.dispatch_alarm"
                  name="chek-dispatch-alarm"
                  switch
              >
              </b-form-checkbox>
            </b-form-group>
          </validation-provider>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2 text-capitalize"
              type="submit"
            >
              {{ isAdd ? $t("actions.add") : $t("actions.edit") }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
              class="text-capitalize"
            >
              {{ $t("actions.cancel") }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BFormCheckbox,
  BInputGroupAppend,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import { computed, ref,onMounted } from "@vue/composition-api";
import { required, alphaNum, email, positive, required_if } from "@validations";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import store from "@/store";

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BInputGroupAppend,
    BButton,
    vSelect,
    BFormCheckbox,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: "isSidebarActive",
    event: "update:is-sidebar-active",
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
    },
    isAdd: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      required,
      alphaNum,
      email,
      positive,
    };
  },
  setup(props, { emit }) {
    //HOOKS
    onMounted(async () => await getTypeAlarms());

    //COMPOSABLES
    const toast = useToast();

    //DATA
    const blankData = {
      id_type_alarm: null,
      name: null,
      description: null,
      priority: 0,
      dispatch_alarm: false,
    };

    const itemData = ref(JSON.parse(JSON.stringify(blankData)));

    const typeAlarms = ref([]);

    //METHODS
    const getTypeAlarms = async () => {
      await store.dispatch("type_alarm/getAll", { max: 100 })
        .then((response) => {
            typeAlarms.value = response.data;
        })
        .catch((error) => {
            typeAlarms.value = [];
        });
    }

    const resetData = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData));
    }

    const onSubmit = async () => {
      const req = {
        id: itemData.value.id,
        params: itemData.value,
      };

      await store.dispatch("pacom_alarm_types/edit", req)
        .then((response) => {
          if (response.success) {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "CheckIcon",
                variant: "success",
              },
            });
            emit("refetch-data");
            emit("update:is-sidebar-active", false);
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .catch((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: response.response.data.message,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    }

    const { refFormObserver, getValidationState, resetForm } = formValidation(resetData);

    const loadData = () => {
      if (!props.isAdd) {
        itemData.value = props.itemEdit;
      }
    };


    return {
      itemData,
      onSubmit,

      refFormObserver,
      getValidationState,
      loadData,
      resetForm,
      typeAlarms
    };
  },
};
</script>
